/**
 * Breadcrumb Component
 * 
 * A breadcrumb navigation component showing the current page hierarchy.
 */

import React from 'react';
import { Box, Typography, Link, Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { colors, typography as typographyTokens } from '../../tokens';

export interface BreadcrumbItem {
  /** Display label */
  label: string;
  /** Navigation path (optional, if clickable) */
  href?: string;
  /** Click handler (optional) */
  onClick?: () => void;
}

export interface BreadcrumbProps {
  /** Array of breadcrumb items */
  items: BreadcrumbItem[];
  /** Additional styles */
  sx?: SxProps<Theme>;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  sx,
}) => (
  <MuiBreadcrumbs
    separator={
      <NavigateNextIcon
        sx={{
          fontSize: 18,
          color: colors.neutral[400],
        }}
      />
    }
    sx={{
      '& .MuiBreadcrumbs-ol': {
        alignItems: 'center',
      },
      ...sx,
    }}
  >
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      const isClickable = Boolean(item.href || item.onClick);

      if (isLast || !isClickable) {
        return (
          <Typography
            key={item.label}
            variant="body2"
            sx={{
              color: isLast ? colors.neutral[900] : colors.neutral[500],
              fontWeight: isLast 
                ? typographyTokens.fontWeight.medium 
                : typographyTokens.fontWeight.normal,
              fontSize: typographyTokens.fontSize.sm,
            }}
          >
            {item.label}
          </Typography>
        );
      }

      return (
        <Link
          key={item.label}
          href={item.href}
          onClick={(e) => {
            if (item.onClick) {
              e.preventDefault();
              item.onClick();
            }
          }}
          underline="hover"
          sx={{
            color: colors.neutral[500],
            fontSize: typographyTokens.fontSize.sm,
            fontWeight: typographyTokens.fontWeight.normal,
            cursor: 'pointer',
            '&:hover': {
              color: colors.primary[500],
            },
          }}
        >
          {item.label}
        </Link>
      );
    })}
  </MuiBreadcrumbs>
);

export default Breadcrumb;

